import React from 'react';
import { Button } from '@/components/ui/button';
import { X, Plus, Minus } from 'lucide-react';
import { tx } from '@/translations/translations';
import { displaySize } from '@/utils/displaySize';
import { useProducts } from '@/hooks/useProducts';

export const CartItemRow = ({ item, idx, language, onUpdateQuantity, onRemove }) => {
  const { products } = useProducts();
  const live = products.find(p => p.id === item.id);
  const displayName = live?.name || item.name;
  const colorName = item.selectedColor
    ? (live?.colors?.find(c => c.value === item.selectedColor.value)?.name || item.selectedColor.name)
    : null;
  const traditionLabels = { ashkenazi: tx(language, 'Ashkenazi', 'Ashkenazi', 'אשכנזי'), sefaradi: tx(language, 'Sefaradí', 'Sephardic', 'ספרדי'), no_amarrados: tx(language, 'No Amarrados', 'Not Tied', 'לא קשורים') };
  const hilosLabels = {
    avodat_yad_grueso: tx(language, 'A Mano Gruesos', 'Handmade Thick', 'עבודת יד עבים'),
    avodat_yad_delgado: tx(language, 'A Mano Delgados', 'Handmade Thin', 'עבודת יד דקים'),
    mechona_delgado: tx(language, 'A Máquina', 'Machine', 'מכונה'),
    sin_hilos: tx(language, 'Sin Hilos', 'No Threads', 'ללא חוטים')
  };
  const cottonLabels = { cotton_smooth: tx(language, 'Liso', 'Cotton Smooth', 'חלק'), cotton_edge: tx(language, 'Acanalado', 'Cotton Ribbed', 'מצולע') };

  return (
    <div className="border rounded-lg p-4" data-testid={`sidebar-cart-item-${idx}`}>
      <div className="flex gap-4">
        {/* Image */}
        <div className="w-20 h-20 rounded-md overflow-hidden bg-white ring-1 ring-slate-200 flex-shrink-0 flex items-center justify-center p-1">
          <img
            src={item.image}
            alt={displayName}
            className="max-w-full max-h-full object-contain"
          />
        </div>

        {/* Details */}
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-start">
            <div>
              <h4 className="font-semibold text-slate-900 truncate text-sm">
                {displayName}
              </h4>
              <div className="text-xs text-slate-500 mt-0.5 space-y-0.5">
                <p>{tx(language, 'Talla', 'Size', 'מידה')}: {displaySize(item.size, language)}</p>
                {item.selectedColor && (
                  <p>{tx(language, 'Color', 'Color', 'צבע')}: {colorName}</p>
                )}
                {item.selectedTradition && !item.noTzitzit && (
                  <p>{tx(language, 'Tradición', 'Tradition', 'מסורת')}: {traditionLabels[item.selectedTradition]}</p>
                )}
                {item.selectedHilos && !item.noTzitzit && (
                  <p>{tx(language, 'Hilos', 'Threads', 'חוטים')}: {hilosLabels[item.selectedHilos]}</p>
                )}
                {item.selectedCottonType && item.hasCottonType && (
                  <p>{tx(language, 'Tipo algodón', 'Cotton type', 'סוג כותנה')}: {cottonLabels[item.selectedCottonType]}</p>
                )}
                {item.customSelections && Object.entries(item.customSelections).map(([k, v]) => (
                  <p key={k}>{k}: {v}</p>
                ))}
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 -mt-1"
              onClick={() => onRemove(item)}
              data-testid={`remove-item-${idx}`}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex justify-between items-center mt-2">
            {/* Quantity Controls */}
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="icon"
                className="h-7 w-7"
                onClick={() => onUpdateQuantity(item, item.quantity - 1)}
                data-testid={`qty-minus-${idx}`}
              >
                <Minus className="h-3 w-3" />
              </Button>
              <span className="text-sm font-medium w-8 text-center">
                {item.quantity}
              </span>
              <Button
                variant="outline"
                size="icon"
                className="h-7 w-7"
                onClick={() => onUpdateQuantity(item, item.quantity + 1)}
                data-testid={`qty-plus-${idx}`}
              >
                <Plus className="h-3 w-3" />
              </Button>
            </div>

            {/* Price */}
            <span className="font-bold text-cyan-600">
              ${(item.price * item.quantity).toFixed(2)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
